import { Box, Divider, Link, Typography } from "@mui/material";

import xIcon from "../../assets/icons/x.svg";
import appstoreIcon from "../../assets/icons/appstore.svg";
import facebookIcon from "../../assets/icons/facebook.svg";
import instagramIcon from "../../assets/icons/instagram.svg";
import googleplayIcon from "../../assets/icons/googleplay.svg";
import goalngoFooterLogo from "../../assets/icons/goalngo-footer-logo.svg";

export const Footer = () => {
  const tagline = "Connecting football opportunities worldwide.";
  const menuItems = defaultMenuItems;
  const bottomLinks = defaultBottomLinks;
  const copyright = "© 2025 Goalngo. All rights reserved.";

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 4, md: 6 },
        fontFamily: "Montserrat, sans-serif",
        backgroundColor: "#f9fafb",
      }}
    >
      <Box sx={{ maxWidth: 1200, mx: "auto", px: { xs: 3, md: 0 } }}>
        <Box component="footer" sx={{ display: "flex", flexDirection: "column", gap: { xs: 3, md: 4 } }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-between",
              gap: { xs: 3, md: 4 },
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                maxWidth: { xs: "100%", md: 320 },
              }}
            >
              <Link
                href={logo.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  textDecoration: "none",
                  color: "inherit",
                }}
              >
                <Box component="img" src={logo.src} alt={logo.alt} sx={{ height: 48, width: "auto" }} />
              </Link>
              <Typography
                sx={{
                  maxWidth: 260,
                  fontSize: "0.9rem",
                  color: "#475467",
                  fontWeight: 500,
                  lineHeight: 1.4,
                }}
              >
                {tagline}
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                {storeLinks.map((item, index) => (
                  <>
                    <Link
                      key={item.label}
                      href={item.url}
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 0,
                        height: 32,
                      }}
                    >
                      <Box
                        component="img"
                        src={item.src}
                        alt={item.alt}
                        sx={{ height: "40px", width: "140px", aspectRatio: "140/40" }}
                      />
                    </Link>
                    {index < storeLinks.length - 1 && <Divider orientation="vertical" flexItem />}
                  </>
                ))}
              </Box>
              <Box sx={{ display: "flex", gap: 1, mt: "40px" }}>
                {socialLinks.map((item) => (
                  <Link
                    key={item.label}
                    href={item.url}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 0,
                      width: 36,
                      height: 36,
                    }}
                    aria-label={item.label}
                  >
                    <Box component="img" src={item.src} alt={item.alt} sx={{ height: "27px", aspectRatio: "1/1" }} />
                  </Link>
                ))}
              </Box>
            </Box>
            <Box
              sx={{
                flex: 1,
                display: "grid",
                maxWidth: "800px",
                gridTemplateColumns: { xs: "repeat(2, minmax(0,1fr))", md: "repeat(4, minmax(0,1fr))" },
                gap: 2,
              }}
            >
              {menuItems.map((section, sectionIdx) => (
                <Box key={sectionIdx}>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      mb: 1,
                      color: "#111827",
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      fontSize: "1.2rem",
                    }}
                  >
                    {section.title}
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                    {section.links.map((link) => (
                      <Link
                        key={link.url}
                        href={link.url}
                        underline="hover"
                        sx={{ fontSize: "1rem", lineHeight: 1.4, textTransform: "capitalize", color: "#475467" }}
                      >
                        {link.text}
                      </Link>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
          <Box
            sx={{
              borderTop: "1px solid #e5e7eb",
              pt: 3,
              mt: 3,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              fontSize: "0.85rem",
              color: "#475467",
            }}
          >
            <Typography sx={{ fontSize: "0.85rem", color: "#475467" }}>{copyright}</Typography>
            <Box sx={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
              {bottomLinks.map((link) => (
                <Link key={link.url} href={link.url} underline="hover" sx={{ fontSize: "0.85rem", color: "#0f172a" }}>
                  {link.text}
                </Link>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const logo = {
  src: goalngoFooterLogo,
  alt: "Goalngo logo",
  url: "https://www.goalngo.com/",
};

const defaultMenuItems = [
  {
    title: "Main Pages",
    links: [
      { text: "home", url: "https://www.goalngo.com/" },
      { text: "Goalngo Network", url: "https://www.goalngo.com/goalngo-network" },
      { text: "Goalngo Business", url: "https://www.goalngo.com/business-app" },
      { text: "GoVerse", url: "https://www.goalngo.com/goverse" },
    ],
  },
  {
    title: "Products",
    links: [
      {
        text: "Discover & Promote",
        url: "https://www.goalngo.com/goalngo-products/discover-promote-talents",
      },
      {
        text: "Support & Invest",
        url: "https://www.goalngo.com/goalngo-products/sponsor-invest-in-football-talents",
      },
      {
        text: "Offer Services",
        url: "https://www.goalngo.com/goalngo-products/offer-services",
      },
      {
        text: "Find & Recruit",
        url: "https://www.goalngo.com/goalngo-products/scout-recruit-talents",
      },
      {
        text: "Payments & Marketplace",
        url: "https://www.goalngo.com/goalngo-products/payments-marketplace",
      },
      {
        text: "Build Fan Network",
        url: "https://www.goalngo.com/goalngo-products/fan-engagement",
      },
      {
        text: "Organize Events",
        url: "https://www.goalngo.com/goalngo-products/organize-events",
      },
    ],
  },
  {
    title: "Solutions",
    links: [
      {
        text: "Clubs & Academics",
        url: "https://www.goalngo.com/solutions/clubs-academics",
      },
      {
        text: "National Sports Entities",
        url: "https://www.goalngo.com/solutions/national-sports-entities",
      },
      {
        text: "Football Professionals",
        url: "https://www.goalngo.com/solutions/football-professionals",
      },
      {
        text: "Football Talents",
        url: "https://www.goalngo.com/solutions/football-talents",
      },
      {
        text: "Football Fans",
        url: "https://www.goalngo.com/solutions/football-fans",
      },
    ],
  },
  {
    title: "Resources",
    links: [
      { text: "Insights", url: "https://www.goalngo.com/goalngos-insights" },
      { text: "Invest in Goalngo", url: "https://www.goalngo.com/invest" },
      { text: "FAQs", url: "https://www.goalngo.com/faqs" },
      { text: "Contact Us", url: "https://www.goalngo.com/contact-us" },
      { text: "Privacy Policy", url: "https://www.goalngo.com/privacy-policy" },
      { text: "Terms and Conditions", url: "https://www.goalngo.com/terms-conditions" },
    ],
  },
];

const defaultBottomLinks = [
  { text: "Privacy Policy", url: "https://www.goalngo.com/privacy-policy" },
  { text: "Terms and Conditions", url: "https://www.goalngo.com/terms-conditions" },
];

const socialLinks = [
  { label: "X", url: "https://x.com/GoalngoTech", src: xIcon, alt: "Goalngo on X" },
  { label: "Instagram", url: "https://www.instagram.com/goalngo_/", src: instagramIcon, alt: "Goalngo on Instagram" },
  {
    label: "Facebook",
    url: "https://www.facebook.com/goalngo.technologies.co",
    src: facebookIcon,
    alt: "Goalngo on Facebook",
  },
];

const storeLinks = [
  {
    label: "Google Play",
    url: "https://play.google.com/store/apps/details?id=com.goalngo_tech.goalngo&pcampaignid=web_share",
    src: googleplayIcon,
    alt: "Download Goalngo on Google Play",
  },
  {
    label: "App Store",
    url: "https://apps.apple.com/eg/app/goalngo/id6443919098",
    src: appstoreIcon,
    alt: "Download Goalngo on the App Store",
  },
];
